import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Star, MapPin, CreditCard, Calendar, Users } from "lucide-react";
import { memo, useState, useCallback } from "react";

const features = [
  {
    icon: Shield,
    title: "Profesionales Verificados",
    description: "Todos los entrenadores pasan por un proceso de verificación de credenciales y certificaciones.",
    color: "bg-blue-500"
  },
  {
    icon: Star,
    title: "Valoraciones Reales",
    description: "Opiniones auténticas de clientes de tu comunidad para tomar la mejor decisión.",
    color: "bg-yellow-500"
  },
  {
    icon: MapPin,
    title: "Cerca de Ti",
    description: "Encuentra profesionales en tu zona. Entrena en casa, en el parque o en el gimnasio.",
    color: "bg-green-500"
  },
  {
    icon: CreditCard,
    title: "Pagos Seguros",
    description: "Reserva y paga online de forma segura. Sin sorpresas, sin comisiones ocultas.",
    color: "bg-purple-500"
  },
  {
    icon: Calendar,
    title: "Reserva Fácil",
    description: "Sistema de reservas integrado. Elige día y hora, confirma y listo.",
    color: "bg-pink-500"
  },
  {
    icon: Users,
    title: "Comunidad Activa",
    description: "Únete a miles de personas que ya transformaron su vida con nuestros profesionales.",
    color: "bg-orange-500"
  },
];

export const WhyChooseUs = memo(() => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Image with optimized loading */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-muted">
              <img 
                src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=800&h=600&fit=crop&q=80"
                alt="Entrenamiento personalizado"
                className={`w-full h-[500px] object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                decoding="async"
                onLoad={handleImageLoad}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              
              {/* Floating stats */}
              <div className="absolute bottom-6 left-6 right-6 flex justify-between">
                <div className="bg-background/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                  <p className="text-3xl font-bold text-primary">98%</p>
                  <p className="text-sm text-muted-foreground">Clientes satisfechos</p>
                </div>
                <div className="bg-background/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                  <p className="text-3xl font-bold text-primary">24h</p>
                  <p className="text-sm text-muted-foreground">Respuesta media</p>
                </div>
              </div>
            </div>
            
            {/* Decorative */}
            <div className="absolute -z-10 top-10 -left-10 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute -z-10 -bottom-10 -right-10 w-60 h-60 rounded-full bg-accent/20 blur-3xl" />
          </div>

          {/* Right - Features */}
          <div>
            <Badge variant="secondary" className="mb-4">¿Por qué Ekselencia?</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              La Plataforma que <span className="gradient-text">Transforma Vidas</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              No somos solo un directorio. Somos un ecosistema completo que conecta personas 
              con los mejores profesionales del fitness y la salud.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <Card key={index} className="border-0 shadow-none bg-muted/50 hover:bg-muted transition-colors">
                  <CardContent className="p-4 flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${feature.color}`}>
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{feature.title}</h3>
                      <p className="text-sm text-muted-foreground">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
